import React, { useState } from 'react';
import { 
  QrCode, Search, Printer, Baby, ShieldCheck, 
  Thermometer, ChevronRight, CheckCircle2, RefreshCw, Monitor, 
  X, Zap, Lock, UserPlus, Users, Check, BellRing, Settings
} from 'lucide-react';

// ✅ IMPORTACIONES CON RUTAS ABSOLUTAS
import { useGlobal } from 'context/GlobalContext';
import RegistroInvitado from 'pages/recepcion/RegistroInvitado';

// Canales de comunicación en tiempo real
const canalTV = new BroadcastChannel('iglekids_tv');
const canalNotificaciones = new BroadcastChannel('iglekids_app_padres');

const ModuloRecepcion = () => {
  const { ninos, registrarEntrada, registrarSalida } = useGlobal();

  // --- ESTADOS DE SISTEMA ---
  const [modo, setModo] = useState('entrada');
  const [busqueda, setBusqueda] = useState('');
  const [escaneando, setEscaneando] = useState(false);
  const [mostrandoInvitado, setMostrandoInvitado] = useState(false);
  const [flash, setFlash] = useState(false);
  const [ninoSeleccionado, setNinoSeleccionado] = useState(null);
  const [procesoExitoso, setProcesoExitoso] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [errorDuplicado, setErrorDuplicado] = useState(null);
  const [tutorRetira, setTutorRetira] = useState(null);
  const [impresoraSel, setImpresoraSel] = useState("");

  const [estadoFisico, setEstadoFisico] = useState({ temperatura: '36.5', grupoHoy: '', hallazgos: [] });

  const catalogoHallazgos = [
    { id: 'raspado', label: 'RASPÓN', emoji: '🩹', color: 'bg-orange-50 text-orange-600 border-orange-200' },
    { id: 'picadura', label: 'PICADURA', emoji: '🦟', color: 'bg-red-50 text-red-600 border-red-200' },
    { id: 'tos', label: 'TOS', emoji: '😷', color: 'bg-blue-50 text-blue-600 border-blue-200' },
    { id: 'escurrimiento', label: 'FLUJO NASAL', emoji: '🤧', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    { id: 'ojos', label: 'OJOS ROJOS', emoji: '👁️', color: 'bg-rose-50 text-rose-600 border-rose-200' },
    { id: 'animo', label: 'DECAÍDO', emoji: '😴', color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  ];

  // --- LÓGICA DE CONTROL ---
  const seleccionarNino = (nino) => {
    if (modo === 'entrada' && nino.estatus === 'en_salon') {
      setErrorDuplicado(`¡ALERTA! ${nino.nombre} ya está en salón.`);
      return;
    }
    if (modo === 'salida' && nino.estatus !== 'en_salon') {
      setErrorDuplicado(`ERROR: ${nino.nombre} no registra entrada.`);
      return;
    }
    setNinoSeleccionado(nino);
    setEstadoFisico({...estadoFisico, grupoHoy: nino.salon, hallazgos: []});
    setTutorRetira(null); 
    setErrorDuplicado(null);
  };

  const ejecutarAccion = async () => {
    if (modo === 'entrada' && !impresoraSel) {
      alert("⚠️ Error: Selecciona una terminal de impresión.");
      return;
    }
    if (modo === 'salida' && !tutorRetira) {
      alert("⚠️ Seguridad: Selecciona quién retira al niño.");
      return;
    }

    setImprimiendo(true);

    const ticketID = Math.random().toString(36).substr(2, 4).toUpperCase();

    if (modo === 'entrada') {
      await registrarEntrada(ninoSeleccionado.id, {
        temperatura: estadoFisico.temperatura,
        notas: estadoFisico.hallazgos.join(', ')
      }, ticketID);
      setTimeout(() => window.print(), 300);
    } else {
      await registrarSalida(ninoSeleccionado.id);
      canalNotificaciones.postMessage({
        tipo: 'NOTIFICACION_SALIDA',
        mensaje: `Iglekids: ${ninoSeleccionado.nombre} retirado por ${tutorRetira}.`,
      });
    }

    canalTV.postMessage({
      tipo: modo === 'entrada' ? 'BIENVENIDA' : 'DESPEDIDA',
      nombre: ninoSeleccionado.nombre,
      salon: estadoFisico.grupoHoy,
    });

    setTimeout(() => {
      setImprimiendo(false);
      setProcesoExitoso(true);
      setTimeout(() => { 
        setNinoSeleccionado(null); 
        setProcesoExitoso(false); 
        setBusqueda('');
      }, 2000);
    }, 1500);
  };

  if (mostrandoInvitado) return <RegistroInvitado alCerrar={() => setMostrandoInvitado(false)} />;

  return (
    <div className="w-full max-w-[1400px] mx-auto animate-in fade-in duration-500 pb-10 font-sans text-left">
      
      {/* TICKET DE SEGURIDAD (OCULTO EN PANTALLA) */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #ticket-area, #ticket-area * { visibility: visible; }
          #ticket-area { position: absolute; left: 0; top: 0; width: 80mm; padding: 5mm; }
        }
      `}</style>
      
      <div id="ticket-area" className="hidden print:block bg-white text-black font-mono text-center">
        <div className="border-b-4 border-black pb-4 mb-4">
          <h2 className="text-2xl font-black italic">CASA DE FE</h2>
          <p className="text-[10px] uppercase tracking-widest font-bold">Seguridad Infantil Iglekids</p>
        </div>
        <div className="space-y-6">
          <h1 className="text-4xl font-black uppercase leading-none">{ninoSeleccionado?.nombre.split(' ')[0]}</h1>
          <div className="border-y-2 border-black py-2 flex justify-between text-xs font-bold uppercase">
            <span>{estadoFisico.grupoHoy}</span>
            <span>#{ninoSeleccionado?.ticket || 'TEMP'}</span>
          </div>
          <div className="py-6 flex flex-col items-center">
             <div className="w-40 h-40 border-8 border-black flex items-center justify-center font-black text-6xl">
               {ninoSeleccionado?.ticket || 'OK'}
             </div>
          </div>
        </div>
      </div>

      {/* MODAL DE ERROR CRÍTICO */}
      {errorDuplicado && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
          <div className="bg-rose-600 text-white p-10 rounded-[4rem] shadow-2xl flex flex-col items-center gap-6 border-8 border-white max-w-lg text-center animate-in zoom-in-95">
            <Lock size={80} strokeWidth={2.5} /> 
            <p className="font-black italic text-3xl uppercase leading-none tracking-tighter">{errorDuplicado}</p>
            <button onClick={() => setErrorDuplicado(null)} className="mt-4 px-10 py-4 bg-white text-rose-600 rounded-2xl font-black uppercase text-xs tracking-widest">Entendido</button>
          </div>
        </div>
      )}

      {/* PANEL DE CONTROL SUPERIOR */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 px-4 gap-6 no-print">
        <button onClick={() => window.open('/tv', '_blank')} className="flex items-center gap-3 bg-slate-900 text-amber-400 px-8 py-4 rounded-[2rem] font-black text-xs uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all shadow-xl">
          <Monitor size={20} /> Pantalla Pasillo
        </button>

        <div className="flex items-center gap-4 bg-white p-3 rounded-[2rem] shadow-xl border-2 border-slate-50">
           <div className={`p-3 rounded-2xl ${impresoraSel ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'} text-white`}>
             <Settings size={22} />
           </div>
           <select 
             value={impresoraSel} 
             onChange={(e) => setImpresoraSel(e.target.value)} 
             className="bg-transparent font-black text-[11px] uppercase tracking-widest outline-none pr-6 cursor-pointer"
           >
              <option value="">Configurar Impresora</option>
              <option value="ZEBRA_01">Zebra ZD220 - A</option>
              <option value="ZEBRA_02">Zebra ZD220 - B</option>
           </select>
        </div>
      </div>

      {!ninoSeleccionado ? (
        <div className="space-y-10 px-4 text-left no-print">
          <div className="flex justify-center">
            <div className="bg-white p-3 rounded-[3.5rem] shadow-2xl flex gap-3 border-4 border-slate-50">
              <button onClick={() => setModo('entrada')} className={`px-12 py-6 rounded-[2.8rem] font-black uppercase text-sm tracking-[0.2em] transition-all ${modo === 'entrada' ? 'bg-[#0A2440] text-amber-400' : 'text-slate-300'}`}>Ingreso</button>
              <button onClick={() => setModo('salida')} className={`px-12 py-6 rounded-[2.8rem] font-black uppercase text-sm tracking-[0.2em] transition-all ${modo === 'salida' ? 'bg-rose-600 text-white' : 'text-slate-300'}`}>Retiro</button>
            </div>
          </div>

          <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-6">
            <div className="relative flex-1">
              <Search className="absolute left-10 top-1/2 -translate-y-1/2 text-slate-300" size={36} />
              <input 
                type="text" 
                placeholder="BUSCAR EXPLORADOR..." 
                className="w-full pl-24 pr-10 py-10 bg-white rounded-[4rem] shadow-2xl text-3xl font-black outline-none uppercase" 
                value={busqueda} 
                onChange={(e) => setBusqueda(e.target.value)} 
              />
            </div>
            <button onClick={() => setEscaneando(true)} className="px-10 bg-[#0A2440] text-amber-400 rounded-[4rem] shadow-2xl"><QrCode size={40} /></button>
            <button onClick={() => setMostrandoInvitado(true)} className="px-12 bg-indigo-600 text-white rounded-[4rem] shadow-2xl flex items-center gap-4"><UserPlus size={35} /> <span className="font-black text-xs uppercase">Invitado</span></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ninos.filter(n => n.nombre.toLowerCase().includes(busqueda.toLowerCase())).map(n => (
              <button key={n.id} onClick={() => seleccionarNino(n)} className="bg-white p-10 rounded-[4rem] shadow-sm border-2 border-slate-50 hover:shadow-xl transition-all flex justify-between items-center">
                <div className="text-left">
                  <span className="font-black text-2xl text-slate-800 italic uppercase">{n.nombre.split(' ')[0]}</span>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">{n.salon}</p>
                </div>
                {n.estatus === 'en_salon' ? <div className="bg-emerald-500 text-white p-2 rounded-full"><Check size={20} /></div> : <ChevronRight className="text-indigo-200" size={28} />}
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* VISTA DE PROCESAMIENTO */
        <div className="max-w-6xl mx-auto px-4 animate-in zoom-in-95 no-print pb-20">
          <div className="bg-white rounded-[5rem] shadow-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-12 min-h-[700px]">
            <div className={`lg:col-span-5 p-16 text-white flex flex-col items-center justify-center text-center space-y-10 ${modo === 'entrada' ? 'bg-[#0A2440]' : 'bg-rose-600'}`}>
              <div className="w-48 h-48 bg-white/10 rounded-[4rem] flex items-center justify-center backdrop-blur-xl border-4 border-white/20">
                <Baby size={100} />
              </div>
              <h3 className="text-5xl font-black italic uppercase tracking-tighter">{ninoSeleccionado.nombre}</h3>
            </div>
            
            <div className="lg:col-span-7 p-20 flex flex-col justify-center space-y-12">
              {modo === 'entrada' ? (
                <div className="space-y-10">
                  <div className="grid grid-cols-2 gap-8">
                    <div className="bg-slate-50 p-8 rounded-[3rem]">
                      <p className="text-[10px] font-black text-slate-400 uppercase mb-4 tracking-widest">Temperatura</p>
                      <input type="text" value={estadoFisico.temperatura} onChange={(e) => setEstadoFisico({...estadoFisico, temperatura: e.target.value})} className="bg-transparent text-5xl font-black w-full outline-none italic" />
                    </div>
                    <div className="bg-slate-50 p-8 rounded-[3rem]">
                      <p className="text-[10px] font-black text-slate-400 uppercase mb-4 tracking-widest">Confirmar Salón</p>
                      <span className="text-2xl font-black text-indigo-600 uppercase italic">{estadoFisico.grupoHoy}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {catalogoHallazgos.map(item => (
                      <button key={item.id} onClick={() => setEstadoFisico({...estadoFisico, hallazgos: estadoFisico.hallazgos.includes(item.id) ? estadoFisico.hallazgos.filter(h => h !== item.id) : [...estadoFisico.hallazgos, item.id]})} className={`p-6 rounded-[2.5rem] border-4 transition-all flex flex-col items-center gap-3 ${estadoFisico.hallazgos.includes(item.id) ? `${item.color} border-current` : 'bg-slate-50 border-transparent opacity-40'}`}>
                        <span className="text-4xl">{item.emoji}</span>
                        <span className="text-[10px] font-black uppercase">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-10">
                  <div className="bg-rose-50 p-10 rounded-[4rem] border-4 border-rose-100">
                    <h3 className="font-black uppercase text-xl italic text-rose-600 mb-6">Tutor que retira:</h3>
                    {ninoSeleccionado.tutores?.map((t, idx) => (
                      <button key={idx} onClick={() => setTutorRetira(t.nombre)} className={`w-full p-7 rounded-[2rem] flex items-center justify-between border-4 mb-4 ${tutorRetira === t.nombre ? 'bg-rose-600 border-rose-700 text-white' : 'bg-white border-rose-50'}`}>
                        <span className="font-black italic uppercase text-2xl">{t.nombre}</span>
                        {tutorRetira === t.nombre && <Check size={28} />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={ejecutarAccion} disabled={imprimiendo} className={`w-full py-10 text-white rounded-[3.5rem] font-black text-2xl uppercase tracking-widest shadow-2xl transition-all border-b-[12px] ${modo === 'entrada' ? 'bg-[#0A2440] border-[#061626]' : 'bg-rose-600 border-rose-800'}`}>
                {imprimiendo ? <RefreshCw className="animate-spin" size={32} /> : (modo === 'salida' ? <BellRing size={36} /> : <Printer size={36} />)} 
                {modo === 'salida' ? 'CONFIRMAR SALIDA' : 'GENERAR INGRESO'}
              </button>
              <button onClick={() => setNinoSeleccionado(null)} className="text-[11px] font-black text-slate-400 uppercase tracking-[0.5em]">Volver</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuloRecepcion;